import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Row, Col, Alert } from 'react-bootstrap';
import { Users, AtSign, RefreshCw, Cpu } from 'lucide-react';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { useSettings } from '../../../../context/SettingsContext';

// "Möt teamet" — presents Finn's built-in specialists (name, @handle, role, what they help with)
// + any custom agents the tenant created. Data comes from the platform roster (GET /agent-roster),
// so names/personas stay in sync with what the chat actually routes to.

// Pre-generated cartoon headshots shipped under public/ (served as-is, NOT processed by
// webpack/SVGR — the DiceBear SVGs use namespaced metadata that SVGR can't compile). Keyed by
// handle; custom agents (no asset) fall back to a gradient initial.
const AVATAR_BASE = `${process.env.PUBLIC_URL || ''}/agent-avatars`;
const AGENT_AVATARS = {
  '@finn': `${AVATAR_BASE}/finn.svg`,
  '@paige': `${AVATAR_BASE}/paige.svg`,
  '@reece': `${AVATAR_BASE}/reece.svg`,
  '@harper': `${AVATAR_BASE}/harper.svg`,
  '@cruz': `${AVATAR_BASE}/cruz.svg`,
  '@vera': `${AVATAR_BASE}/vera.svg`,
  '@mats': `${AVATAR_BASE}/mats.svg`,
  '@sage': `${AVATAR_BASE}/sage.svg`,
  '@quinn': `${AVATAR_BASE}/quinn.svg`,
};
const hashCode = (s) => [...String(s)].reduce((acc, c) => (acc * 31 + c.charCodeAt(0)) >>> 0, 7);

const AgentAvatar = ({ seed, initial, size = 48 }) => {
  const asset = AGENT_AVATARS[seed];
  if (asset) {
    return <img src={asset} width={size} height={size} alt={String(seed)} style={{ flexShrink: 0, borderRadius: 12 }} />;
  }
  // Custom agents have no pre-generated avatar → gradient initial.
  const h = hashCode(seed) % 360;
  const id = `agav-${String(seed).replace(/[^a-z0-9]/gi, '')}`;
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" style={{ flexShrink: 0 }} aria-hidden="true">
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={`hsl(${h}, 70%, 58%)`} />
          <stop offset="100%" stopColor={`hsl(${(h + 38) % 360}, 72%, 44%)`} />
        </linearGradient>
      </defs>
      <rect width="48" height="48" rx="12" fill={`url(#${id})`} />
      <text x="24" y="25" textAnchor="middle" dominantBaseline="central" fontSize="20" fontWeight="700" fill="#fff">{initial}</text>
    </svg>
  );
};

const MODEL_LABELS = {
  'claude-sonnet-4-6': 'Claude Sonnet 4.6',
  'claude-opus-4-8': 'Claude Opus 4.8',
  'claude-haiku-4-5-20251001': 'Claude Haiku 4.5',
};
const modelLabel = (m) => MODEL_LABELS[m] || m || '—';

const AgentCard = ({ a }) => (
  <Col md={6} className="mb-3">
    <div className="modern-card h-100">
      <div className="modern-card-body d-flex gap-3" style={{ padding: '18px 20px' }}>
        <AgentAvatar seed={a.handle} initial={String(a.displayName || '?').charAt(0).toUpperCase()} />
        <div style={{ minWidth: 0 }}>
          <div className="d-flex align-items-center gap-2" style={{ flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600 }}>{a.displayName}</span>
            <span style={{ fontSize: '0.78rem', color: '#6b7280', display: 'inline-flex', alignItems: 'center' }}>
              <AtSign size={11} />{String(a.handle).replace(/^@/, '')}
            </span>
          </div>
          <div style={{ fontSize: '0.78rem', fontWeight: 500, color: 'var(--primary)', marginBottom: 4 }}>{a.role}</div>
          <div style={{ fontSize: '0.83rem', color: '#4b5563', lineHeight: 1.45 }}>{a.blurb}</div>
          {a.model && (
            <div style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 8px', borderRadius: 12, background: '#f3f4f6', color: '#6b7280', fontSize: '0.72rem' }}>
              <Cpu size={11} /> {modelLabel(a.model)}
            </div>
          )}
        </div>
      </div>
    </div>
  </Col>
);

const MeetTheTeamSection = () => {
  const { companyId } = useSettings();
  const [loading, setLoading] = useState(true);
  const [roster, setRoster] = useState([]);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(getApiUrl(`${companyId}/agent-roster`), { withCredentials: true });
      setRoster(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Kunde inte ladda teamet just nu.');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="text-center py-5">
        <RefreshCw className="spin" size={24} />
        <p className="text-muted mt-2">Laddar teamet…</p>
      </div>
    );
  }

  const builtIns = roster.filter((a) => a.builtIn);
  const customs = roster.filter((a) => !a.builtIn);

  return (
    <>
      <div className="modern-card" style={{ background: 'linear-gradient(135deg, #f0f4ff 0%, #e8f0fe 100%)', border: '1px solid #d0daf0' }}>
        <div className="modern-card-body d-flex align-items-center gap-3" style={{ padding: '24px 28px' }}>
          <div style={{ width: 52, height: 52, borderRadius: 14, background: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2563eb' }}>
            <Users size={26} />
          </div>
          <div>
            <h2 className="mb-1" style={{ fontSize: '1.35rem', fontWeight: 600 }}>Möt teamet</h2>
            <p className="mb-0 text-muted" style={{ fontSize: '0.9rem' }}>
              Finns specialister. I chatten kan du skriva <strong>@</strong> följt av namnet för att prata direkt med någon av dem (t.ex. <strong>@reece</strong>).
            </p>
          </div>
        </div>
      </div>

      {error && <Alert variant="warning" className="mt-3">{error}</Alert>}

      {/* Vad Finn vet (Phase 4) — the trust surface. */}
      <div className="modern-card mt-3">
        <div className="modern-card-body d-flex align-items-center justify-content-between gap-3" style={{ padding: '18px 24px' }}>
          <div>
            <strong>Vad Finn vet</strong>
            <p className="mb-0 text-muted" style={{ fontSize: '0.9rem' }}>
              Se, rätta eller ta bort varje uppgift assistenten har lärt sig om er verksamhet.
            </p>
          </div>
          <Link className="btn btn-outline-primary btn-sm" to={`/${companyId}/vad-finn-vet`}>
            Öppna översikten
          </Link>
        </div>
      </div>

      <Row className="mt-3">
        {builtIns.map((a) => <AgentCard key={a.handle} a={a} />)}
      </Row>

      {customs.length > 0 && (
        <>
          <h6 className="text-muted mt-2 mb-3" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
            Egna agenter
          </h6>
          <Row>
            {customs.map((a) => <AgentCard key={a.handle} a={a} />)}
          </Row>
        </>
      )}
    </>
  );
};

export default MeetTheTeamSection;
